import { defaults, has } from 'lodash/fp'
import { buffers } from 'redux-saga'
import { inflate } from 'pako'
import { actionChannel, call, put, select, take, takeEvery } from 'redux-saga/effects'
import { getConnectionInfo } from '../../../auth/interface/selectors'
import { platforms } from '../../../constants'
import { logManager } from '../../../logs'
import * as actions from '../../interface/actions'
import * as actionTypes from '../../interface/actionTypes'
import { getNotificationConfig, getNotificationsInfo } from '../../interface/selectors'
import * as requests from '../requests'

// Get the logger
const log = logManager.getLogger('NOTIFICATIONS')

/**
 * Unregister for android push notification.
 * @method enableWebsocketChannelEntry
 */
export function * enableWebsocketChannelEntry () {
  yield takeEvery(actionTypes.ENABLE_NOTIFICATION_CHANNEL, enableWebsocketChannel)
}

function * enableWebsocketChannel (action) {
  if (action.payload.enable) {
    // TODO: If websockets are not connected, connect them here.
  }

  // TODO: Handle possible error case when connecting websockets.
  //      Otherwise, plain dispatch to update state.
  yield put(
    actions.enableNotificationChannelFinish(action.meta.channel, {
      params: action.payload
    })
  )
}

const INITIAL_BUFFER_SIZE = 10

export function * processNotification () {
  var queue = []

  const externalNotifications = yield actionChannel(
    actionTypes.PROCESS_NOTIFICATION,
    buffers.expanding(INITIAL_BUFFER_SIZE)
  )
  while (true) {
    const action = yield take(externalNotifications)
    const config = yield select(getNotificationConfig)
    log.info(
      `Received notification on channel ${action.meta.channel} for platform: ${action.meta.platform}; Handling...`
    )

    // Only process notifications from enabled channels, ie. "silence" the channel.
    const channel = yield select(getNotificationsInfo, action.meta.channel)
    if (!channel.channelEnabled) {
      log.debug(`Notification received on disabled channel: ${action.meta.channel}. Ignoring it ...`)
      continue
    }

    const notification = action.payload
    const notificationMessage = notification.notificationMessage
    const notificationId = id(action.meta.platform)(notification)(notificationMessage)

    let formattedPayload = action.payload
    if (has('payload.notificationMessage.sessionParams.sdpFormat', action)) {
      log.debug('Notification contains SDP. Normalizing.')
      formattedPayload = yield call(normalizeSDP, action.payload)
    }

    // If we're in the PUSH-only notification mode and
    // this incoming notification is coming through a channel other than PUSH channel (e.g. WEBSOCKET) and
    // this notification is for an incoming call
    if (
      formattedPayload.notificationMessage.eventType === 'call' &&
      config.incomingCallNotificationMode === 'push-channel-only' &&
      action.meta.channel !== 'PUSH'
    ) {
      // Ignore this call-related notification, but at a minimum generate a log so that user is aware.
      log.warn(
        'Dropped incoming call notification with id: ' +
          notificationId +
          ' received through channel: ' +
          action.meta.channel +
          ' because current mode for handling notifications is set to: push-channel-only.'
      )
      continue
    } else {
      // Don't check for duplicate notifications if idCacheLength is set to 0
      // Get the list of "relevant" IDs in the queue ("relevant" being the latest X elements).
      // This covers edge-cases where the cache length config is changed (to a smaller number)
      //    after the queue has begun to be populated.
      const ids = config.idCacheLength === 0 ? [] : queue.slice(config.idCacheLength * -1)
      // If the notification ID is part of the list, then it's a duplicate.
      const duplicate = ids.includes(notificationId)
      if (duplicate) {
        log.info('Notification was a duplicate; ignoring.')
        const error = new Error(`Notification id ${notificationId} is duplicate.`)
        // TODO: Tech-debt; this action should be a notificationReceived error action.
        //      But that requires all sagas listening for notifications to filter out
        //      error actions ..which requires their take() patterns changed, which
        //      is another tech-debt item.
        yield put(actions.processNotificationFinish(error))
      } else {
        queue.push(notificationId)
        // Because the client can update the value of idCacheLength at any time
        // we have to adjust the size of our queue accordingly
        while (queue.length > config.idCacheLength) {
          queue.shift()
        }

        const { platform, channel } = action.meta
        // Inform all other plugins by sending a NOTIFICATION_RECEIVED action
        yield put(actions.notificationReceived(formattedPayload, platform, channel))
      }
    }
  }
}

const id = platform => notification => notificationMessage => {
  switch (platform) {
    case platforms.LINK:
      log.debug(`The received notification is of type ${notificationMessage.eventType}.`)
      return notificationMessage.eventId

    case platforms.UC:
      // A Link notification can be in either the Link format or the SPiDR format (for calls).
      return notificationMessage.id || notificationMessage.eventId

    case platforms.CPAAS:
      // CPaaS notifications do not have a consistent path to their ID.
      //    The ID is one level down in the object, but we need to dynamically
      //    find the key that its in.
      for (const key in notification) {
        if (notification[key].id) {
          return notification[key].id
        }
      }
      log.error('Received notification without a findable ID: ', Object.keys(notification))
      return null

    default:
      log.debug('Received notification from unknown platform.')
      return null
  }
}

/**
 * This function accepts a notification payload. If the payload is a spidr payload
 * and contains an sdpFormat that needs manipulating, it is done here.
 * @method normalizeSDP
 * @param payload A processNotification action payload
 * @returns payload A processNotification action payload
 */
export function * normalizeSDP (payload) {
  // Copy the payload to not disturb the action.
  payload = { ...payload }

  if (payload.notificationMessage.sessionParams.sdpFormat === 'compressed') {
    log.debug('Found SDP format: compressed. Deflating compressed SDP...')
    // convert based64 encoded string into bytes
    const sdpCompressedBytes = atob(payload.notificationMessage.sessionParams.sdp)

    // First convert the ASCII string into an array of unsigned integers
    // pako.inflate() requires an Uint8Array as parameter.
    const buf = new ArrayBuffer(sdpCompressedBytes.length)
    const bufView = new Uint8Array(buf) // allocate enough space ..
    for (let i = 0, strLen = sdpCompressedBytes.length; i < strLen; i++) {
      bufView[i] = sdpCompressedBytes.charCodeAt(i) // fill-in the array of uint(s)
    }

    // uncompressed the bytes
    try {
      const sdpUnCompressedBytes = inflate(bufView)
      // convert uncompressed bytes back into string

      const sdpString = String.fromCharCode.apply(null, sdpUnCompressedBytes)

      payload.notificationMessage.sessionParams.sdp = sdpString
      log.debug(`Returning uncompressed SDP as part of payload: ${sdpString}`)
    } catch (err) {
      log.debug(`Cannot inflate the compressed SDP: ${err}`)
      // TODO: If there was an error, try to handle it here.
    }
    return payload
  } else if (payload.notificationMessage.sessionParams.sdpFormat === 'url') {
    const connection = yield select(getConnectionInfo)
    const { pushRegistration } = yield select(getNotificationConfig)
    log.debug(`Found SDP format: url. pushRegistration: ${pushRegistration}`)
    // If a push registration endpoint was configured, use that instead of default.
    if (pushRegistration) {
      connection.server = defaults(connection.server, pushRegistration)
      connection.protocol = defaults(connection.protocol, pushRegistration)
      connection.port = defaults(connection.port, pushRegistration)
      connection.version = defaults(connection.version, pushRegistration)
    }
    log.info('Fetching SDP...')
    const response = yield call(requests.fetchSDP, connection, payload.notificationMessage.sessionParams.sdp)
    if (response.error) {
      // REST request failed.
      // TODO: Handle the error here rather than throwing an error
    } else {
      payload.notificationMessage.sessionParams.sdp = response.eventDataResponse.sdp
    }
    return payload
  } else {
    log.debug(`Unknown sdpFormat received: ${payload.notificationMessage.sessionParams.sdpFormat}.`)
    return payload
  }
}
